/*
 * DiagCharts.js — Pure-SVG chart primitives for the Diagnostics panels.
 *
 * No external charting library needed. Every component is a controlled,
 * animated React + SVG visual with smooth CSS transitions.
 *
 * Components exported:
 *   TimeSeriesChart   — multi-line area chart with gradient fill & tooltip
 *   ArcGauge          — 270° arc gauge with animated needle
 *   DonutChart        — donut/pie with animated segments & legend
 *   HeatMap           — WAN × Class compliance heat map
 *   BarChart          — horizontal bar chart with labels
 *   SparkArea         — tiny inline area sparkline
 *   MiniKPI           — single-value KPI tile with trend arrow
 *   QoSTimeline       — event timeline strip (switch events)
 */
import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";

// ── Palette ──────────────────────────────────────────────────────────────────
const PALETTE = [
  "#6366f1", "#06b6d4", "#22c55e", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#64748b",
];
const pickColor = (i) => PALETTE[i % PALETTE.length];

// ── Helpers ──────────────────────────────────────────────────────────────────
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
const lerp = (a, b, t) => a + (b - a) * t;
const fmt = (v) => {
  if (v == null) return "—";
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(1);
};

// ══════════════════════════════════════════════════════════════════════════════
// TimeSeriesChart — multi-series line + area with tooltip, grid, & legend
// ══════════════════════════════════════════════════════════════════════════════

export function TimeSeriesChart({
  series = [],       // [{ label, data: number[], color? }]
  width = 520,
  height = 200,
  yLabel = "",
  yMin,
  yMax,
  gridLines = 4,
  showLegend = true,
  showDots = false,
  areaOpacity = 0.12,
  animate = true,
}) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null); // { idx, x, y }
  const pad = { top: 16, right: 16, bottom: 24, left: 48 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  // Compute domain
  const { realMin, realMax, allEmpty } = useMemo(() => {
    let min = Infinity, max = -Infinity, empty = true;
    for (const s of series) {
      for (const v of s.data) {
        if (v != null) { min = Math.min(min, v); max = Math.max(max, v); empty = false; }
      }
    }
    if (empty) return { realMin: 0, realMax: 1, allEmpty: true };
    const pad2 = (max - min) * 0.1 || 1;
    return {
      realMin: yMin != null ? yMin : Math.max(0, min - pad2),
      realMax: yMax != null ? yMax : max + pad2,
      allEmpty: false,
    };
  }, [series, yMin, yMax]);

  const maxLen = useMemo(() => Math.max(...series.map((s) => s.data.length), 2), [series]);

  const toX = (i) => pad.left + (i / (maxLen - 1)) * plotW;
  const toY = (v) => pad.top + plotH - ((v - realMin) / (realMax - realMin)) * plotH;

  const buildPath = (data, close) => {
    const pts = data.map((v, i) => `${toX(i).toFixed(1)},${toY(v ?? 0).toFixed(1)}`);
    let d = `M${pts.join("L")}`;
    if (close) d += `L${toX(data.length - 1).toFixed(1)},${toY(realMin).toFixed(1)}L${toX(0).toFixed(1)},${toY(realMin).toFixed(1)}Z`;
    return d;
  };

  const onMouseMove = useCallback(
    (e) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mx = e.clientX - rect.left;
      const idx = Math.round(((mx - pad.left) / plotW) * (maxLen - 1));
      if (idx >= 0 && idx < maxLen) setHover({ idx, x: toX(idx), y: e.clientY - rect.top });
    },
    [maxLen, plotW, pad.left], // eslint-disable-line
  );

  const gridYs = useMemo(() => {
    const arr = [];
    for (let i = 0; i <= gridLines; i++) {
      const v = lerp(realMin, realMax, i / gridLines);
      arr.push({ v, y: toY(v) });
    }
    return arr;
  }, [realMin, realMax, gridLines]); // eslint-disable-line

  const uid = useMemo(() => `ts-${Math.random().toString(36).slice(2, 8)}`, []);

  return (
    <div className="chart-ts-wrap">
      <svg
        ref={svgRef}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="chart-ts-svg"
        onMouseMove={onMouseMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {series.map((s, i) => (
            <linearGradient key={i} id={`${uid}-g${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color || pickColor(i)} stopOpacity={areaOpacity} />
              <stop offset="100%" stopColor={s.color || pickColor(i)} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>

        {/* Grid */}
        {gridYs.map((g, i) => (
          <g key={i}>
            <line x1={pad.left} y1={g.y} x2={width - pad.right} y2={g.y} className="chart-grid-line" />
            <text x={pad.left - 6} y={g.y + 4} className="chart-axis-label" textAnchor="end">
              {fmt(g.v)}
            </text>
          </g>
        ))}

        {/* Y label */}
        {yLabel && (
          <text x={14} y={pad.top + plotH / 2} className="chart-y-label" transform={`rotate(-90,14,${pad.top + plotH / 2})`}>
            {yLabel}
          </text>
        )}

        {/* Areas + Lines */}
        {series.map((s, i) => (
          <g key={i}>
            <path d={buildPath(s.data, true)} fill={`url(#${uid}-g${i})`} className={animate ? "chart-area-anim" : ""} />
            <path d={buildPath(s.data, false)} fill="none" stroke={s.color || pickColor(i)} strokeWidth="2"
              className={animate ? "chart-line-anim" : ""} strokeLinejoin="round" strokeLinecap="round" />
            {showDots && s.data.map((v, j) => (
              v != null && <circle key={j} cx={toX(j)} cy={toY(v)} r={2.5} fill={s.color || pickColor(i)} className="chart-dot" />
            ))}
          </g>
        ))}

        {/* Hover crosshair */}
        {hover && (
          <>
            <line x1={hover.x} y1={pad.top} x2={hover.x} y2={pad.top + plotH} className="chart-crosshair" />
            {series.map((s, i) => {
              const v = s.data[hover.idx];
              return v != null ? (
                <circle key={i} cx={hover.x} cy={toY(v)} r={4} fill={s.color || pickColor(i)} stroke="#fff" strokeWidth="2" />
              ) : null;
            })}
          </>
        )}
      </svg>

      {/* Tooltip */}
      {hover && (
        <div className="chart-tooltip" style={{ left: hover.x + 8, top: 8 }}>
          {series.map((s, i) => {
            const v = s.data[hover.idx];
            return v != null ? (
              <div key={i} className="chart-tooltip-row">
                <span className="chart-tooltip-dot" style={{ background: s.color || pickColor(i) }} />
                <span className="chart-tooltip-label">{s.label}</span>
                <span className="chart-tooltip-val">{fmt(v)}</span>
              </div>
            ) : null;
          })}
        </div>
      )}

      {/* Legend */}
      {showLegend && series.length > 1 && (
        <div className="chart-legend">
          {series.map((s, i) => (
            <span key={i} className="chart-legend-item">
              <span className="chart-legend-swatch" style={{ background: s.color || pickColor(i) }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ArcGauge — 270° arc gauge with animated value
// ══════════════════════════════════════════════════════════════════════════════

export function ArcGauge({
  value = 0,
  max = 100,
  label = "",
  unit = "",
  size = 120,
  thresholds, // [{ pct, color }] e.g. [{pct: .5, color:'#f59e0b'}, {pct: .8, color:'#22c55e'}]
  color,
}) {
  const r = (size - 16) / 2;
  const cx = size / 2;
  const cy = size / 2 + 6;
  const startAngle = 135;
  const sweep = 270;
  const pct = clamp(value / (max || 1), 0, 1);

  const arcPath = (startDeg, endDeg) => {
    const s = ((startDeg - 90) * Math.PI) / 180;
    const e = ((endDeg - 90) * Math.PI) / 180;
    const x1 = cx + r * Math.cos(s);
    const y1 = cy + r * Math.sin(s);
    const x2 = cx + r * Math.cos(e);
    const y2 = cy + r * Math.sin(e);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return `M${x1},${y1}A${r},${r} 0 ${large} 1 ${x2},${y2}`;
  };

  const resolveColor = () => {
    if (color) return color;
    if (!thresholds) {
      if (pct >= 0.7) return "#22c55e";
      if (pct >= 0.4) return "#f59e0b";
      return "#ef4444";
    }
    let c = "#ef4444";
    for (const t of thresholds) {
      if (pct >= t.pct) c = t.color;
    }
    return c;
  };

  const endAngle = startAngle + sweep * pct;
  const fillColor = resolveColor();

  return (
    <div className="chart-gauge-wrap" style={{ width: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={arcPath(startAngle, startAngle + sweep)} fill="none" stroke="var(--c-border, #e2e8f0)" strokeWidth="8" strokeLinecap="round" />
        <path
          d={arcPath(startAngle, endAngle)}
          fill="none" stroke={fillColor} strokeWidth="8" strokeLinecap="round"
          className="chart-gauge-fill"
          style={{ "--gauge-end": `${pct * 100}%` }}
        />
      </svg>
      <div className="chart-gauge-center">
        <div className="chart-gauge-value" style={{ color: fillColor }}>{fmt(value)}<small>{unit}</small></div>
        <div className="chart-gauge-label">{label}</div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// DonutChart — animated donut with optional center label
// ══════════════════════════════════════════════════════════════════════════════

export function DonutChart({
  segments = [],   // [{ label, value, color? }]
  size = 180,
  thickness = 28,
  centerLabel = "",
  centerValue = "",
  showLegend = true,
  maxLegend = 8,
}) {
  const total = useMemo(() => segments.reduce((s, seg) => s + (seg.value || 0), 0), [segments]);
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  const cx = size / 2;
  const cy = size / 2;

  // Build segment offsets
  const arcs = useMemo(() => {
    if (total === 0) return [];
    let offset = 0;
    return segments.map((seg, i) => {
      const pct = seg.value / total;
      const dashLen = pct * circ;
      const gap = circ - dashLen;
      const startOffset = -offset * circ;
      offset += pct;
      return { ...seg, pct, dashLen, gap, startOffset, color: seg.color || pickColor(i) };
    });
  }, [segments, total, circ]);

  // Sort legend by value descending, cap at maxLegend
  const legendItems = useMemo(() => {
    return [...arcs].sort((a, b) => b.value - a.value).slice(0, maxLegend);
  }, [arcs, maxLegend]);

  return (
    <div className="chart-donut-wrap">
      <div className="chart-donut-ring" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="chart-donut-svg">
          {/* Background ring */}
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--c-border, #e2e8f0)" strokeWidth={thickness} />
          {arcs.map((a, i) => (
            <circle
              key={i}
              cx={cx} cy={cy} r={r}
              fill="none"
              stroke={a.color}
              strokeWidth={thickness}
              strokeDasharray={`${a.dashLen} ${a.gap}`}
              strokeDashoffset={a.startOffset}
              transform={`rotate(-90 ${cx} ${cy})`}
              className="chart-donut-seg"
              style={{ "--seg-delay": `${i * 60}ms` }}
            />
          ))}
        </svg>
        <div className="chart-donut-center">
          <div className="chart-donut-center-val">{centerValue || fmt(total)}</div>
          <div className="chart-donut-center-lbl">{centerLabel || "Total"}</div>
        </div>
      </div>

      {showLegend && legendItems.length > 0 && (
        <div className="chart-donut-legend">
          {legendItems.map((a, i) => (
            <div key={i} className="chart-donut-legend-row">
              <span className="chart-legend-swatch" style={{ background: a.color }} />
              <span className="chart-donut-legend-label">{a.label}</span>
              <span className="chart-donut-legend-val">{fmt(a.value)}</span>
              <span className="chart-donut-legend-pct">{(a.pct * 100).toFixed(1)}%</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HeatMap — WAN × Class compliance grid
// ══════════════════════════════════════════════════════════════════════════════

export function HeatMap({
  rows = [],    // [{ label, cells: [{ value: 0-100, label?, ok? }] }]
  columns = [], // string[] — column headers
  colorScale,   // fn(value) → color; default: green/yellow/red
}) {
  const defaultScale = (v) => {
    if (v >= 70) return "#22c55e";
    if (v >= 40) return "#f59e0b";
    return "#ef4444";
  };
  const scale = colorScale || defaultScale;

  return (
    <div className="chart-heatmap-wrap">
      <table className="chart-heatmap">
        <thead>
          <tr>
            <th />
            {columns.map((c, i) => <th key={i}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              <td className="chart-heatmap-row-label">{row.label}</td>
              {row.cells.map((cell, ci) => (
                <td key={ci}>
                  <div
                    className="chart-heatmap-cell"
                    style={{ backgroundColor: scale(cell.value), opacity: 0.85 }}
                    title={cell.label || `${cell.value}`}
                  >
                    {cell.ok !== undefined ? (cell.ok ? "✓" : "✗") : Math.round(cell.value)}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BarChart — horizontal bars
// ══════════════════════════════════════════════════════════════════════════════

export function BarChart({
  items = [],   // [{ label, value, color? }]
  maxValue,
  height = 24,
  showValues = true,
  formatValue,
}) {
  const max = maxValue || Math.max(...items.map((i) => i.value || 0), 1);
  const fmtVal = formatValue || fmt;

  return (
    <div className="chart-bar-wrap">
      {items.map((item, i) => {
        const pct = clamp((item.value || 0) / max * 100, 0, 100);
        return (
          <div key={i} className="chart-bar-row" style={{ height }}>
            <span className="chart-bar-label">{item.label}</span>
            <div className="chart-bar-track">
              <div
                className="chart-bar-fill"
                style={{
                  width: `${pct}%`,
                  backgroundColor: item.color || pickColor(i),
                  "--bar-delay": `${i * 40}ms`,
                }}
              />
            </div>
            {showValues && <span className="chart-bar-value">{fmtVal(item.value)}</span>}
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SparkArea — tiny inline area chart (for table cells / KPI tiles)
// ══════════════════════════════════════════════════════════════════════════════

export function SparkArea({
  data = [],
  width = 100,
  height = 28,
  color = "var(--accent, #6366f1)",
  fill = true,
}) {
  if (data.length < 2) return <div style={{ width, height }} />;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = `M${pts.join("L")}`;
  const area = `${line}L${width},${height}L0,${height}Z`;

  return (
    <svg width={width} height={height} className="chart-spark">
      {fill && <path d={area} fill={color} opacity="0.1" />}
      <polyline points={pts.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {data.length > 0 && (
        <circle
          cx={(width).toFixed(1)}
          cy={(height - ((data[data.length - 1] - min) / range) * (height - 4) - 2).toFixed(1)}
          r="3" fill={color}
          className="chart-spark-dot"
        />
      )}
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MiniKPI — compact value tile with optional sparkline & trend
// ══════════════════════════════════════════════════════════════════════════════

export function MiniKPI({
  label,
  value,
  unit = "",
  trend,    // "up" | "down" | "flat" | null
  trendGood, // is the trend direction good? (green if true, red if false)
  sparkData, // number[] for inline spark
  color,
}) {
  const trendIcon = trend === "up" ? "↑" : trend === "down" ? "↓" : trend === "flat" ? "→" : "";
  const trendColor = trendGood ? "#22c55e" : trendGood === false ? "#ef4444" : "var(--text-muted)";

  return (
    <div className="chart-kpi">
      <div className="chart-kpi-label">{label}</div>
      <div className="chart-kpi-row">
        <span className="chart-kpi-value" style={color ? { color } : undefined}>
          {value}<small>{unit}</small>
        </span>
        {trendIcon && (
          <span className="chart-kpi-trend" style={{ color: trendColor }}>{trendIcon}</span>
        )}
      </div>
      {sparkData && sparkData.length > 2 && (
        <SparkArea data={sparkData} width={80} height={20} color={color || "var(--accent)"} />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// QoSTimeline — horizontal event markers
// ══════════════════════════════════════════════════════════════════════════════

export function QoSTimeline({ events = [], width = 520, height = 64 }) {
  const sorted = useMemo(() => {
    return [...events].sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [events]);

  if (events.length === 0) return <div className="chart-timeline-empty">No switch events.</div>;

  const tMin = new Date(sorted[0].time).getTime();
  const tMax = new Date(sorted[sorted.length - 1].time).getTime();
  const span = tMax - tMin || 1;

  return (
    <div className="chart-timeline-wrap">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="chart-timeline-svg">
        {/* Base line */}
        <line x1={16} y1={height / 2} x2={width - 16} y2={height / 2} className="chart-timeline-line" />
        {/* Event dots */}
        {sorted.map((ev, i) => {
          const x = 16 + ((new Date(ev.time).getTime() - tMin) / span) * (width - 32);
          return (
            <g key={i}>
              <circle cx={x} cy={height / 2} r={6} fill="#ef4444" className="chart-timeline-dot" />
              <text x={x} y={height / 2 - 12} className="chart-timeline-label" textAnchor="middle">
                {ev.from_wan}→{ev.to_wan}
              </text>
              <text x={x} y={height / 2 + 20} className="chart-timeline-time" textAnchor="middle">
                {new Date(ev.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// useTimeSeries — ring-buffer hook for accumulating polled data over time
// ══════════════════════════════════════════════════════════════════════════════

export function useTimeSeries(maxLen = 60) {
  const ref = useRef({});
  const [, forceUpdate] = useState(0);

  const push = useCallback((key, value) => {
    if (!ref.current[key]) ref.current[key] = [];
    const arr = ref.current[key];
    arr.push(value);
    if (arr.length > maxLen) arr.shift();
    forceUpdate((n) => n + 1);
  }, [maxLen]);

  const get = useCallback((key) => ref.current[key] || [], []);
  const getAll = useCallback(() => ({ ...ref.current }), []);
  const reset = useCallback(() => { ref.current = {}; forceUpdate((n) => n + 1); }, []);

  return { push, get, getAll, reset };
}
